import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSteps from '@/components/ProcessSteps';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Shield, Search, FileCheck, AlertCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function ClearanceSearchServices() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Shield,
      title: 'Risk Mitigation',
      description: 'Identify potential conflicts before filing your trademark application.',
    },
    {
      icon: Search,
      title: 'Comprehensive Analysis',
      description: 'Thorough search of federal, state, and common law trademarks.',
    },
    {
      icon: FileCheck,
      title: 'Expert Review',
      description: 'Professional analysis of search results and conflict assessment.',
    },
    {
      icon: AlertCircle,
      title: 'Cost Savings',
      description: 'Avoid costly rejections and legal disputes down the road.',
    },
  ];

  const faqs = [
    {
      question: 'What is a trademark clearance search?',
      answer: 'A trademark clearance search is a comprehensive investigation to determine if your proposed trademark is available for use and registration. It searches existing federal, state, and common law trademarks to identify potential conflicts.',
    },
    {
      question: 'Why do I need a clearance search?',
      answer: 'A clearance search helps you avoid investing time and money in a trademark that may be rejected or could infringe on existing rights. It identifies potential issues before you file your application.',
    },
    {
      question: 'How long does a clearance search take?',
      answer: 'Typically, a comprehensive clearance search takes 3-5 business days to complete, including the analysis and report preparation.',
    },
    {
      question: 'What databases are searched?',
      answer: 'We search the USPTO federal database, state trademark databases, business registries, domain names, and common law sources to provide a thorough analysis.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
            >
              Clearance Search Services
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Conduct thorough trademark searches to ensure your brand is clear for registration and use.
            </p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Choose Our Clearance Search?
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Make informed decisions with comprehensive trademark clearance searches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <PricingTable />

      {/* FAQ Section */}
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Get answers to common questions about clearance searches.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Clear Your Trademark?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Start with a comprehensive clearance search to protect your brand investment.
          </p>
          <button
            onClick={() => setShowDialog(true)}
            className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="clearance_search" />

      <Footer />
    </div>
  );
}